import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GridColumn as Column, Grid } from "@progress/kendo-react-grid";
import { setStageList } from "../../../../../actions/projectSidebar";
import { button, number } from "../../../../../config";
import { GridContext, onSortChange, reordering, sortable } from "../../../../../utils/kendo";
import { initialSort } from "../../../projectSidebar.constants";
import "../../../projectSidebar.scss";
import { Drag, EditDeleteCell, StageNameCell } from "./ProjectStagesGridCell";
import { editSorting } from "./services";

/**
 * ProjectStagesGrid component is responsible for setting up an stages for project
 * Used from team settings, user profile sidebar, and related views.
 * @author Bhavana
 */
const ProjectStagesGrid = ({ projectId }) => {
  const { stageList, projectDetails } = useSelector((state) => state.projectSidebar);
  const { RoleId } = projectDetails || {};
  const [data, setData] = useState([]);
  const [sort, setSort] = useState(initialSort);
  const [activeItem, setActiveItem] = useState(null);
  const [hover, setHover] = useState({ id: null });
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  /**
   * Sorts and updates the stage list data when stageList changes.
   * Sorts stages based on isFinalStage flag, with non-final stages appearing first.
   * @author Bhavana
   */
  useEffect(() => {
    if (stageList?.length > number.ZERO) {
      const sortedStageList = [...stageList].sort((a, b) => a.isFinalStage - b.isFinalStage);
      setData(sortedStageList);
    }
  }, [stageList]);

  /**
   * Adds a new stage to the data array.
   * @author Bhavana
   */
  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newStage = { id: Date.now(), label: "", inEdit: true, isFinalStage: 0 };
    const updatedData = [...data];
    updatedData.splice(updatedData.length - 1, 0, newStage);
    setData(updatedData);
  };

  /**
   * Reorders the data array based on drag and drop actions.
   * @param {Object} dataItem - The item being reordered
   * @author Bhavana
   */
  const reorder = (dataItem) => {
    const reorderedData = reordering(dataItem, activeItem, data);
    if (Array.isArray(reorderedData)) {
      setData([...reorderedData]);
      dispatch(setStageList([...reorderedData]));
      const values = reorderedData?.map((item) => item?.id)?.join(",");
      const payload = {
        entityIds: values,
        entityName: "Project Stages",
        projectId: projectId,
      };
      dispatch(editSorting(payload));
    }
  };

  /**
   * Sets the active item when drag starts.
   * @author Bhavana
   */
  const dragStart = (dataItem) => {
    setActiveItem(dataItem);
  };

  /**
   * Renders a table row with mouse enter/leave handlers to set hover state.
   * @author Bhavana
   */
  const customRowRender = (tr, props) =>
    React.cloneElement(tr, {
      onMouseEnter: () => setHover({ id: props.dataItem.id }),
      onMouseLeave: () => setHover({ id: null }),
    });

  const stagesContextValue = { reorder, dragStart, data, setData, projectId, dispatch, stageList, hover };
  return (
      <GridContext.Provider value={stagesContextValue}>
          <div className='w-100 d-flex justify-content-end align-items-center mt-3 mb-2'>
            <button
              className='btn btn-sm btn-primary'
              id='project-stages-grid-add-new-stage-btn'
              onClick={handleAdd}
              disabled={!auth?.user?.isAdmin && projectId !== auth?.user?.myProjectId && RoleId !== number?.FOUR}>
              {button?.ADD_NEW_STAGE}
            </button>
          </div>
        <Grid data={data} sortable={sortable} sort={sort} onSortChange={(event) => onSortChange(event, setSort)} rowRender={customRowRender}>
          <Column cell={Drag} width='40px' />
          <Column field='stageName' title='Stage Name' cell={StageNameCell} minResizableWidth={number?.TWO_HUNDRED} />
          {(RoleId === number?.FOUR || projectId === auth?.user?.myProjectId || auth?.user?.isAdmin) && <Column title='' width='70px' cell={EditDeleteCell} />}
        </Grid>
      </GridContext.Provider>
  );
};

export default ProjectStagesGrid;
